import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { countries } from '@/data/countries';

export type CountryValueKey = 'alpha-2' | 'country-code';

interface CountryMultiSelectProps {
  countryCodes: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
  valueKey?: CountryValueKey;
}

/**
 * Get country flag image URL from country code
 */
const getCountryFlagUrl = (countryCode: string): string => {
  const code = countryCode.toLowerCase();
  return `https://flagcdn.com/w20/${code}.png`;
};

/**
 * Get country name from country code
 */
const getCountryName = (countryCode: string): string => {
  const country = countries.find((c) => c['alpha-2'] === countryCode.toUpperCase());
  return country ? country.name : countryCode;
};

/**
 * Create country options with flag URLs
 */
const createCountryOptions = (countryCodes: string[], valueKey: CountryValueKey = 'alpha-2') => {
  const countryMap = new Map<string, (typeof countries)[0]>();
  countries.forEach((country) => {
    countryMap.set(country['alpha-2'], country);
  });

  return countryCodes
    .map((code) => {
      const country = countryMap.get(code.toUpperCase());
      if (!country) return null;
      return {
        label: country.name,
        value: country[valueKey],
        alpha2: country['alpha-2'],
        flagUrl: getCountryFlagUrl(country['alpha-2']),
      };
    })
    .filter((opt): opt is NonNullable<typeof opt> => opt !== null);
};

const createSelectedSet = (selected: string[]) => new Set(selected);
const isSelected = (selectedSet: Set<string>, value: string) => selectedSet.has(value);
const addValue = (selected: string[], value: string) => [...selected, value];
const removeValue = (selected: string[], value: string) => selected.filter((v) => v !== value);
const toggleValue = (selected: string[], value: string) => {
  const selectedSet = createSelectedSet(selected);
  return isSelected(selectedSet, value) ? removeValue(selected, value) : addValue(selected, value);
};

const getSelectedOptions = (options: ReturnType<typeof createCountryOptions>, selected: string[]) => {
  const selectedSet = createSelectedSet(selected);
  return options.filter((option) => isSelected(selectedSet, option.value));
};

const hasAnySelected = (selected: string[]) => selected.length > 0;

const getSelectAllText = (hasSelected: boolean, t: (key: string) => string) => (hasSelected ? t('countrySelect.unselectAll') : t('countrySelect.selectAll'));

const getAllValues = (options: ReturnType<typeof createCountryOptions>) => options.map((opt) => opt.value);

const getDisplayText = (
  selectedOptions: ReturnType<typeof createCountryOptions>,
  placeholder: string,
  maxCount = 2,
) => {
  if (selectedOptions.length === 0) {
    return placeholder;
  }
  if (selectedOptions.length > maxCount) {
    return `${selectedOptions.length} selected`;
  }
  return null;
};

/**
 * Country multi-select component with flags
 * Allows selecting multiple countries from a list of country codes
 * Displays country flags in badges and dropdown (consistent with LanguageSelector style)
 */
export const CountryMultiSelect = ({
  countryCodes,
  selected,
  onChange,
  placeholder,
  disabled = false,
  className,
  contentClassName,
  valueKey = 'alpha-2',
}: CountryMultiSelectProps) => {
  const { t } = useTranslation('common');
  const resolvedPlaceholder = placeholder ?? t('countrySelect.searchPlaceholder');
  const [open, setOpen] = React.useState(false);
  const options = createCountryOptions(countryCodes, valueKey);

  const handleToggle = (value: string) => {
    const newSelected = toggleValue(selected, value);
    onChange(newSelected);
  };

  const handleRemove = (value: string) => {
    const newSelected = removeValue(selected, value);
    onChange(newSelected);
  };

  const handleClear = () => {
    onChange([]);
  };

  const handleSelectAll = () => {
    const newSelected = hasAnySelected(selected) ? [] : getAllValues(options);
    onChange(newSelected);
  };

  const selectedSet = createSelectedSet(selected);
  const selectedOptions = getSelectedOptions(options, selected);
  const displayText = getDisplayText(selectedOptions, resolvedPlaceholder, 2);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('h-9 w-full justify-between', className)}
        >
          <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
            {displayText ? (
              <span className="text-muted-foreground truncate">{displayText}</span>
            ) : (
              <>
                {selectedOptions.slice(0, 2).map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="hover:bg-secondary/80 gap-1 pr-0.5"
                  >
                    <img src={option.flagUrl} alt={option.alpha2} className="h-3 w-4 shrink-0 object-cover" />
                    <span className="max-w-[120px] truncate">{option.label}</span>
                    <span
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!disabled) handleRemove(option.value);
                      }}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemove(option.value);
                        }
                      }}
                      aria-disabled={disabled}
                      aria-label={`Remove ${option.label}`}
                      className="ring-offset-background focus:ring-ring ml-1 cursor-pointer rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none"
                    >
                      <XIcon className="size-3" />
                    </span>
                  </Badge>
                ))}
                {selectedOptions.length > 2 && (
                  <Badge variant="secondary" className="pointer-events-none">
                    +{selectedOptions.length - 2}
                  </Badge>
                )}
              </>
            )}
          </div>
          <div className="ml-2 flex shrink-0 items-center gap-2">
            {selected.length > 0 && !disabled && (
              <>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClear();
                    }
                  }}
                  aria-label={t('countrySelect.clearAll')}
                  className="ring-offset-background focus:ring-ring cursor-pointer rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                  <XIcon className="size-4" />
                </span>
                <Separator orientation="vertical" className="h-4" />
              </>
            )}
            <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('z-[10001] flex max-h-[300px] w-full flex-col p-0', contentClassName)}
        align="start"
        side="bottom"
        sideOffset={4}
        onWheel={(e) => {
          // Prevent dialog scroll when scrolling inside popover
          e.stopPropagation();
        }}
      >
        <Command className="flex h-full flex-col overflow-hidden">
          <CommandInput placeholder={t('countrySelect.searchPlaceholder')} className="shrink-0 border-b" />
          <CommandList className="max-h-none flex-1 overflow-y-auto overscroll-contain">
            <CommandEmpty>{t('countrySelect.noCountriesFound')}</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={handleSelectAll} className="text-muted-foreground font-medium">
                {getSelectAllText(hasAnySelected(selected), t)}
              </CommandItem>
              <Separator className="my-1" />
              {options.map((option) => {
                const isOptionSelected = isSelected(selectedSet, option.value);

                return (
                  <CommandItem key={option.value} onSelect={() => handleToggle(option.value)}>
                    <div
                      className={cn(
                        'border-primary mr-2 flex size-4 items-center justify-center rounded-sm border',
                        isOptionSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className="text-primary-foreground size-4" />
                    </div>
                    <img
                      src={option.flagUrl}
                      alt={option.alpha2}
                      className="mr-2 h-3 w-4 shrink-0 object-cover"
                    />
                    <span className="text-foreground">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
